/*
 * CreateMeetingWizard
 * ---------------------------------------------------------------------------
 * SPEC R23 + R27 + R28 (phase 02-meetings v2):
 *   - 3-step modal (Basics → Participants → Agenda) replacing the single-form
 *     create modal and the legacy edit form.
 *   - Same component handles both `mode='create'` and `mode='edit'`.
 *   - Chair picker has NO default selection (R27).
 *   - Agenda links per row hidden behind an "Add links (optional)" disclosure
 *     (R24).
 *
 * Save flow (create):
 *   1. POST /api/v1/gov/meetings (step-1 fields, server generates meeting_code)
 *   2. For each participant_id (excluding chairman — backend auto-inserts the
 *      chair on create per v1 R11): POST /api/v1/gov/meeting-participants
 *   3. For each agenda row: POST /api/v1/gov/meeting-agenda-items with the
 *      `links[]` array (single transactional insert per v1 R8).
 *
 * Save flow (edit):
 *   - Full update sweep on the Step-3 "Update meeting" button:
 *     a. PUT /api/v1/gov/meetings/{id} with all step-1 fields.
 *     b. Diff participant set against the originally-loaded list → POST
 *        new ones, DELETE removed ones.
 *     c. Diff agenda set:
 *         · No id ⇒ POST with embedded links (new agenda).
 *         · Has id ⇒ PUT for field updates; agenda links on existing rows are
 *           left to the inline link manager inside MeetingDetailModal (the
 *           wizard does not own that surface).
 *         · Original id missing from new list ⇒ DELETE.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Check,
  Edit2,
  Trash2,
  Plus,
  X as XIcon,
} from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import EntityLinksMultiField from "../common/EntityLinksMultiField";
import { useFieldValidation } from "../../validation/useFieldValidation";
import { validate as validateCode } from "../../validation/engine";
import {
  ValidatedInput,
  ValidatedTextarea,
  ValidatedDatePicker,
} from "../../validation/ValidatedFields";
import {
  governanceMeetingsService,
  meetingParticipantsService,
  meetingAgendaService,
  GovernanceMeeting,
  MeetingParticipant,
  MeetingAgendaItem,
  MeetingVoteModel,
  MeetingVoteThreshold,
  AvailableUser,
} from "../../services/governanceService";
import {
  agendaLinksService,
  AgendaLink,
  TaskLink,
} from "../../services/tasksService";
import {
  entityRegistryService,
  EntityType,
} from "../../services/entityRegistryService";

// ============================================================================
// Types
// ============================================================================

export interface CreateMeetingWizardProps {
  mode: "create" | "edit";
  isOpen: boolean;
  onClose: () => void;
  onSaved: (meeting: GovernanceMeeting) => void;
  /** Required when mode='edit'. The wizard re-fetches participants + agenda
   *  + agenda-links on open so the meeting prop only needs to carry header
   *  fields. */
  meeting?: GovernanceMeeting;
  /** When true, renders the wizard content inline (no Modal wrapper). Used
   *  to embed the wizard as a tab inside MeetingDetailModal. */
  inline?: boolean;
  /** Inline mode only. When provided, the wizard's action row (Back / Cancel /
   *  Next / Update) is portalled into this element instead of rendered at the
   *  bottom of the body — so the host can place it in the real `Modal.Footer`
   *  and the actions sit in the modal's fixed footer bar, consistent with
   *  every other modal. */
  footerSlot?: HTMLElement | null;
}

interface AgendaDraft {
  /** Server id — present for already-saved agenda rows (edit mode). */
  id?: number;
  agenda_name: string;
  agenda_description?: string;
  agenda_type?: string;
  /** Legacy display name (denormalised); the editable field is presenter_id. */
  presenter?: string;
  /** User id of the presenter — a meeting participant. */
  presenter_id?: number | null;
  duration_minutes?: number;
  display_order?: number;
  /** Local-only flag: true when the user opened the "Add links" disclosure
   *  on this row. Once they add a link the disclosure stays open per R24. */
  _linksOpen?: boolean;
  links: TaskLink[];
}

interface BasicsForm {
  meeting_title: string;
  meeting_type: string;
  chairman_id: number | null;
  scheduled_date: string;
  scheduled_time_from: string;
  scheduled_time_to: string;
  location: string;
  meeting_link: string;
  vote_model: MeetingVoteModel;
  vote_threshold: MeetingVoteThreshold;
  meeting_description: string;
}

// ============================================================================
// Constants
// ============================================================================

const MEETING_TYPE_OPTIONS = [
  { value: "Board", label: "Board" },
  { value: "Committee", label: "Committee" },
  { value: "Review", label: "Review" },
  { value: "Adhoc", label: "Adhoc" },
  { value: "Investigation", label: "Investigation" },
  { value: "Remediation", label: "Remediation" },
  { value: "CAPA", label: "CAPA" },
  { value: "Statutory", label: "Statutory" },
];

const AGENDA_TYPE_OPTIONS = [
  { value: "Discussion", label: "Discussion" },
  { value: "Approval", label: "Approval" },
  { value: "Review", label: "Review" },
  { value: "Information", label: "Information" },
];

const VOTE_MODEL_OPTIONS: { value: MeetingVoteModel; label: string }[] = [
  { value: "BINARY", label: "Upvote / Downvote" },
  { value: "TRINARY", label: "For / Against / Abstain" },
];

const VOTE_THRESHOLD_OPTIONS: { value: MeetingVoteThreshold; label: string }[] = [
  { value: "SIMPLE_MAJORITY", label: "Simple majority" },
  { value: "MAJORITY_WITH_QUORUM", label: "Majority + quorum" },
  { value: "SUPERMAJORITY", label: "Supermajority (≥ 2/3)" },
  { value: "CHAIRMAN_VETO", label: "Chairman Veto" },
];

// Local-date ISO string (toISOString() would shift across midnight in +TZ).
const todayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const nowHHmm = (): string => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Default slot is 10:00–11:00; since the date prefill is today, roll forward
// to the next full hour when 10:00 has already passed so the prefilled time
// is never in the past.
const defaultMeetingTimes = (): { from: string; to: string } => {
  const now = new Date();
  if (now.getHours() < 10) return { from: "10:00", to: "11:00" };
  const fromH = Math.min(now.getHours() + 1, 23);
  const toH = Math.min(fromH + 1, 23);
  return {
    from: `${String(fromH).padStart(2, "0")}:00`,
    to: toH === fromH ? "23:59" : `${String(toH).padStart(2, "0")}:00`,
  };
};

// Factory (not a const) so scheduled_date is today's date at open time, not
// whenever the module was first loaded.
const emptyBasics = (): BasicsForm => ({
  meeting_title: "",
  meeting_type: "",
  chairman_id: null,
  scheduled_date: todayIso(),
  scheduled_time_from: defaultMeetingTimes().from,
  scheduled_time_to: defaultMeetingTimes().to,
  location: "",
  meeting_link: "",
  vote_model: "BINARY",
  vote_threshold: "SIMPLE_MAJORITY",
  meeting_description: "",
});

// Map a meeting row to the Basics form shape. Used both for the lazy initial
// state (so edit mode paints filled fields on the FIRST render — no empty→
// filled flash) and the on-open hydration effect.
const basicsFromMeeting = (m: GovernanceMeeting): BasicsForm => ({
  meeting_title: m.meeting_title || "",
  meeting_type: m.meeting_type || "",
  chairman_id: m.chairman_id ?? null,
  scheduled_date: m.scheduled_date || "",
  scheduled_time_from: (m.scheduled_time_from || "").substring(0, 5) || "10:00",
  scheduled_time_to: (m.scheduled_time_to || "").substring(0, 5) || "11:00",
  location: m.location || "",
  meeting_link: m.meeting_link || "",
  vote_model: m.vote_model || "BINARY",
  vote_threshold: m.vote_threshold || "SIMPLE_MAJORITY",
  meeting_description: m.meeting_description || "",
});

// ============================================================================
// Component
// ============================================================================

const CreateMeetingWizard: React.FC<CreateMeetingWizardProps> = ({
  mode,
  isOpen,
  onClose,
  onSaved,
  meeting,
  inline = false,
  footerSlot,
}) => {
  const vh = useFieldValidation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [form, setForm] = useState<BasicsForm>(() =>
    mode === "edit" && meeting ? basicsFromMeeting(meeting) : emptyBasics(),
  );

  // Participants — wizard tracks user IDs only. The chair (form.chairman_id)
  // is mandatory and pre-marked in the picker but kept OUT of this list — the
  // backend auto-inserts the chair when the meeting is created. In edit mode
  // we also exclude the chair from `participants` so the diff math doesn't
  // try to add/remove them.
  const [participants, setParticipants] = useState<number[]>([]);
  const [agenda, setAgenda] = useState<AgendaDraft[]>([]);

  // Reference snapshots for edit-mode diffing.
  const [originalParticipants, setOriginalParticipants] = useState<
    MeetingParticipant[]
  >([]);
  const [originalAgenda, setOriginalAgenda] = useState<MeetingAgendaItem[]>(
    [],
  );

  // User picker (Step 2)
  const [userOptions, setUserOptions] = useState<AvailableUser[]>([]);
  // Users selected in the picker but not yet added to the participant list.
  // MultiSelect works on (string|number)[] — we add them all on "Add".
  const [pendingUserIds, setPendingUserIds] = useState<(string | number)[]>([]);

  // Agenda link picker — type registry (cached once)
  const [linkableTypeCodes, setLinkableTypeCodes] = useState<string[]>([]);

  // UI flags
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Agenda item editor popup (Step 4). The step now shows a compact summary
  // row per item; add/edit happens in this modal so the step stays a clean
  // list. agendaEditIdx: -1 = creating, >= 0 = editing agenda[idx], null =
  // closed. agendaDraft is a working copy committed to `agenda` only on save.
  const [agendaEditIdx, setAgendaEditIdx] = useState<number | null>(null);
  const [agendaDraft, setAgendaDraft] = useState<AgendaDraft | null>(null);
  // View-first edit: existing agenda items open read-only; "Edit" enables the form.
  const [agendaEditing, setAgendaEditing] = useState(false);

  // ---------------------------------------------------------------------------
  // Lifecycle: reset / preload state when the modal opens
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    vh.reset();
    setStep(1);
    setSaving(false);

    setPendingUserIds([]);
    setAgendaEditIdx(null);
    setAgendaDraft(null);

    if (mode === "create") {
      setForm(emptyBasics());
      setParticipants([]);
      setAgenda([]);
      setOriginalParticipants([]);
      setOriginalAgenda([]);
      return;
    }

    // Edit mode — hydrate from the meeting prop + side-load participants
    // and agenda (including each agenda's link set) from the server so the
    // diff against the new state is computed against real ids.
    if (mode === "edit" && meeting) {
      setForm(basicsFromMeeting(meeting));

      (async () => {
        try {
          setLoadingEdit(true);
          const [parts, agendaRows] = await Promise.all([
            meetingParticipantsService.getByMeeting(meeting.id),
            meetingAgendaService.getByMeeting(meeting.id),
          ]);
          setOriginalParticipants(parts);
          setParticipants(
            parts
              .filter((p) => p.user_id !== meeting.chairman_id)
              .map((p) => p.user_id),
          );
          setOriginalAgenda(agendaRows);

          // Load links for each existing agenda in parallel so the wizard
          // can pre-fill the disclosure with the saved set (display only —
          // the wizard does not own link mutations on already-saved agendas;
          // that surface lives inside MeetingDetailModal).
          const linkPairs = await Promise.all(
            agendaRows.map((a) =>
              agendaLinksService
                .list(meeting.id, a.id)
                .then((ls: AgendaLink[]) => ({ id: a.id, links: ls }))
                .catch(() => ({ id: a.id, links: [] as AgendaLink[] })),
            ),
          );
          const linksById = new Map<number, AgendaLink[]>(
            linkPairs.map((p) => [p.id, p.links]),
          );

          setAgenda(
            agendaRows.map((a) => ({
              id: a.id,
              agenda_name: a.agenda_name,
              agenda_description: a.agenda_description || "",
              agenda_type: a.agenda_type || "Discussion",
              presenter: a.presenter || "",
              presenter_id: a.presenter_id ?? null,
              duration_minutes: a.duration_minutes ?? 15,
              display_order: a.display_order,
              _linksOpen: (linksById.get(a.id) || []).length > 0,
              links: (linksById.get(a.id) || []).map((l) => ({
                id: l.id,
                type_id: l.item_type_id,
                type_code: l.item_type_code ?? null,
                record_id: l.item_record_id,
                label: l.item_label ?? null,
                code: l.item_code ?? null,
              })),
            })),
          );
        } catch (err: any) {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              "Failed to load meeting details",
          );
        } finally {
          setLoadingEdit(false);
        }
      })();
    }
  }, [isOpen, mode, meeting]);

  // ---------------------------------------------------------------------------
  // Side-loads that don't depend on the meeting (run once on open)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    // Load users for picker (Step 2). We use the available-users endpoint
    // un-scoped to a meeting so the same payload works for both modes.
    meetingParticipantsService
      .getAvailableUsers(undefined, undefined)
      .then((users) => setUserOptions(users))
      .catch(() => setUserOptions([]));

    // Load linkable entity types (Step 3 picker). Exclude MEETING and
    // AGENDA_ITEM per v1 R7 / risk register.
    entityRegistryService
      .listTypes(true)
      .then((types: EntityType[]) => {
        setLinkableTypeCodes(
          types
            .filter(
              (t) => t.type_code !== "MEETING" && t.type_code !== "AGENDA_ITEM",
            )
            .map((t) => t.type_code),
        );
      })
      .catch(() => setLinkableTypeCodes([]));
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Option lists (derived)
  // ---------------------------------------------------------------------------
  const userSelectOptions = useMemo(
    () =>
      userOptions.map((u) => ({
        value: String(u.id),
        label: u.user_name || `${u.first_name} ${u.last_name}` || u.login_id,
      })),
    [userOptions],
  );

  const userNameById = useMemo(() => {
    const m = new Map<number, string>();
    userOptions.forEach((u) => {
      m.set(u.id, u.user_name || `${u.first_name} ${u.last_name}` || u.login_id);
    });
    // Edit-mode participants come from the server with their names attached;
    // the available-users picker doesn't always include already-assigned users,
    // so merge these in to avoid "User #<id>" placeholders.
    originalParticipants.forEach((p) => {
      if (p.user_name && !m.has(p.user_id)) m.set(p.user_id, p.user_name);
    });
    return m;
  }, [userOptions, originalParticipants]);

  // Presenter options for an agenda item = the meeting's participants
  // (chairman + added participants), labelled by name.
  const presenterOptions = useMemo(() => {
    const ids: number[] = [];
    if (form.chairman_id != null) ids.push(form.chairman_id);
    participants.forEach((id) => ids.push(id));
    return ids.map((id) => ({
      value: String(id),
      label: userNameById.get(id) || `User #${id}`,
    }));
  }, [form.chairman_id, participants, userNameById]);

  // Pickable user options for Step 2 — exclude the chair (always added by
  // the server / visually pre-marked) and anyone already in the participant
  // list.
  const pickableUserOptions = useMemo(() => {
    const exclude = new Set<number>(participants);
    if (form.chairman_id != null) exclude.add(form.chairman_id);
    return userSelectOptions.filter((o) => !exclude.has(Number(o.value)));
  }, [userSelectOptions, participants, form.chairman_id]);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  const step1Valid = useMemo(() => {
    // Basics now also carries the voting config (vote model + threshold),
    // both of which always have a default, so they add no extra gate here.
    return (
      !!form.meeting_title.trim() &&
      !!form.meeting_type &&
      !!form.scheduled_date &&
      !!form.scheduled_time_from &&
      !!form.scheduled_time_to
    );
  }, [form]);

  const step2Valid = useMemo(() => {
    // Participants step owns the chairman pick. Backend POST /meetings
    // requires chairman_id, so block Next → Agenda until it's set.
    return form.chairman_id != null;
  }, [form.chairman_id]);

  const step3Valid = useMemo(() => {
    // Zero agenda items is explicitly allowed (R23). Each row that DOES
    // exist must have a name.
    return agenda.every((a) => a.agenda_name.trim().length > 0);
  }, [agenda]);

  // ---------------------------------------------------------------------------
  // Step 1 handlers
  // ---------------------------------------------------------------------------
  const setField = <K extends keyof BasicsForm>(key: K, value: BasicsForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ---------------------------------------------------------------------------
  // Step 2 handlers
  // ---------------------------------------------------------------------------
  const handleAddParticipant = () => {
    if (pendingUserIds.length === 0) return;
    const toAdd = pendingUserIds
      .map(Number)
      .filter((id) => !participants.includes(id) && id !== form.chairman_id);
    if (toAdd.length === 0) {
      setPendingUserIds([]);
      return;
    }
    // Dedupe within the batch itself as well.
    setParticipants((prev) => [...prev, ...toAdd.filter((id) => !prev.includes(id))]);
    setPendingUserIds([]);
  };

  const handleRemoveParticipant = (userId: number) => {
    setParticipants((prev) => prev.filter((id) => id !== userId));
  };

  // Promote a participant to chair. The chair is tracked separately in
  // form.chairman_id and rendered from it, so move the new chair OUT of the
  // participants list and move the previous chair (if any) back IN.
  const setAsChair = (userId: number) => {
    const oldChair = form.chairman_id;
    setParticipants((prev) => {
      const withoutNew = prev.filter((id) => id !== userId);
      return oldChair != null && oldChair !== userId
        ? [...withoutNew, oldChair]
        : withoutNew;
    });
    setField("chairman_id", userId);
  };

  // ---------------------------------------------------------------------------
  // Step 3 handlers
  // ---------------------------------------------------------------------------
  const handleRemoveAgendaRow = (idx: number) => {
    setAgenda((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Agenda item editor popup ---
  const openNewAgenda = () => {
    setAgendaDraft({
      agenda_name: "",
      agenda_description: "",
      agenda_type: "Discussion",
      presenter: "",
      presenter_id: null,
      duration_minutes: 15,
      display_order: agenda.length + 1,
      _linksOpen: false,
      links: [],
    });
    setAgendaEditIdx(-1);
    setAgendaEditing(true); // create is always editable
    vh.clearError("agenda_modal_name");
    vh.clearError("agenda_modal_description");
  };

  const openEditAgenda = (idx: number) => {
    // Work on a copy (links array too) so dismissing the modal discards edits.
    setAgendaDraft({ ...agenda[idx], links: [...agenda[idx].links] });
    setAgendaEditIdx(idx);
    setAgendaEditing(false); // open read-only; "Edit" enables the form
    vh.clearError("agenda_modal_name");
    vh.clearError("agenda_modal_description");
  };

  const closeAgendaModal = () => {
    setAgendaEditIdx(null);
    setAgendaDraft(null);
    setAgendaEditing(false);
    vh.clearError("agenda_modal_name");
    vh.clearError("agenda_modal_description");
  };

  const patchDraft = (patch: Partial<AgendaDraft>) =>
    setAgendaDraft((prev) => (prev ? { ...prev, ...patch } : prev));

  const saveAgendaDraft = () => {
    if (!agendaEditing) return;
    if (!agendaDraft || !agendaDraft.agenda_name.trim()) return;
    // Block save on invalid name/description (109/110) — shows inline error.
    const ok = vh.validateAll([
      { name: "agenda_modal_name", code: 109, value: agendaDraft.agenda_name, required: true },
      { name: "agenda_modal_description", code: 110, value: agendaDraft.agenda_description || "" },
    ]);
    if (!ok) return;
    setAgenda((prev) =>
      agendaEditIdx === -1
        ? [...prev, agendaDraft]
        : prev.map((row, i) => (i === agendaEditIdx ? agendaDraft : row)),
    );
    closeAgendaModal();
  };

  // ---------------------------------------------------------------------------
  // Save — create
  // ---------------------------------------------------------------------------
  const buildAgendaCreatePayload = (a: AgendaDraft, meetingId: number) => ({
    meeting_id: meetingId,
    agenda_name: a.agenda_name,
    agenda_description: a.agenda_description || undefined,
    agenda_type: a.agenda_type || undefined,
    presenter_id: a.presenter_id ?? undefined,
    duration_minutes: a.duration_minutes ?? 15,
    display_order: a.display_order,
    // Backend wraps agenda insert + N link inserts in a single transaction
    // (v1 R8). Sending an empty array is equivalent to no links.
    links: a.links.map((l) => ({
      item_type_id: l.type_id,
      item_record_id: l.record_id,
      relationship_type: "Discuss",
    })),
  });

  const handleCreate = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      // 1. Create the meeting. Server generates meeting_code (R3).
      const payload: Partial<GovernanceMeeting> = {
        meeting_title: form.meeting_title.trim(),
        meeting_type: form.meeting_type,
        chairman_id: form.chairman_id ?? undefined,
        scheduled_date: form.scheduled_date,
        scheduled_time_from: form.scheduled_time_from,
        scheduled_time_to: form.scheduled_time_to,
        location: form.location || undefined,
        meeting_link: form.meeting_link || undefined,
        vote_model: form.vote_model,
        vote_threshold: form.vote_threshold,
        meeting_description: form.meeting_description || undefined,
        meeting_status: "DRAFT",
      };
      const created = await governanceMeetingsService.create(payload);

      // 2. Add extra participants (chair is auto-inserted by the backend).
      for (const userId of participants) {
        if (userId === created.chairman_id) continue;
        try {
          await meetingParticipantsService.add({
            meeting_id: created.id,
            user_id: userId,
          });
        } catch (e) {
          // Non-fatal: surface as a soft error but keep going so a single
          // duplicate doesn't block the whole save.
          // eslint-disable-next-line no-console
          console.warn("Failed to add participant", userId, e);
        }
      }

      // 3. Create agenda rows (each carries its initial link set in the
      //    same POST per R8).
      for (const a of agenda) {
        await meetingAgendaService.create(
          buildAgendaCreatePayload(a, created.id),
        );
      }

      onSaved(created);
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to create meeting",
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Save — edit (full sweep)
  // ---------------------------------------------------------------------------
  const handleUpdate = async (): Promise<void> => {
    if (!meeting) return;
    setSaving(true);
    setError(null);
    try {
      // a. PUT header fields.
      const headerPatch: Partial<GovernanceMeeting> = {
        meeting_title: form.meeting_title.trim(),
        meeting_type: form.meeting_type,
        chairman_id: form.chairman_id ?? undefined,
        scheduled_date: form.scheduled_date,
        scheduled_time_from: form.scheduled_time_from,
        scheduled_time_to: form.scheduled_time_to,
        location: form.location || undefined,
        meeting_link: form.meeting_link || undefined,
        vote_model: form.vote_model,
        vote_threshold: form.vote_threshold,
        meeting_description: form.meeting_description || undefined,
      };
      const updated = await governanceMeetingsService.update(
        meeting.id,
        headerPatch,
      );

      // b. Reconcile participants. Compare set-of-user-ids; chair is the
      //    backend's responsibility on create but on edit we explicitly
      //    keep the chair out of the wizard's participant list, so the
      //    diff is symmetric.
      const originalUserIds = new Set(
        originalParticipants
          .filter((p) => p.user_id !== meeting.chairman_id)
          .map((p) => p.user_id),
      );
      const nextUserIds = new Set(participants);

      // Adds.
      for (const userId of Array.from(nextUserIds)) {
        if (!originalUserIds.has(userId)) {
          try {
            await meetingParticipantsService.add({
              meeting_id: meeting.id,
              user_id: userId,
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Failed to add participant", userId, e);
          }
        }
      }
      // Removes.
      for (const orig of originalParticipants) {
        if (orig.user_id === meeting.chairman_id) continue;
        if (!nextUserIds.has(orig.user_id)) {
          try {
            await meetingParticipantsService.remove(orig.id);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("Failed to remove participant", orig.id, e);
          }
        }
      }

      // c. Reconcile agenda. New rows (no id) → POST; existing rows → PUT
      //    field updates; original rows missing from the new set → DELETE.
      const originalById = new Map<number, MeetingAgendaItem>(
        originalAgenda.map((a) => [a.id, a]),
      );
      const nextIds = new Set<number>(
        agenda.filter((a) => a.id != null).map((a) => a.id as number),
      );

      for (let i = 0; i < agenda.length; i++) {
        const a = agenda[i];
        const displayOrder = i + 1;
        if (a.id == null) {
          await meetingAgendaService.create(
            buildAgendaCreatePayload(
              { ...a, display_order: displayOrder },
              meeting.id,
            ),
          );
        } else {
          // PUT only the header-style fields. Links on existing rows are
          // managed by MeetingDetailModal's inline picker (intentional
          // boundary: the wizard's link disclosure for an existing row is
          // read-only display).
          await meetingAgendaService.update(a.id, {
            agenda_name: a.agenda_name,
            agenda_description: a.agenda_description || undefined,
            agenda_type: a.agenda_type || undefined,
            presenter_id: a.presenter_id ?? null,
            duration_minutes: a.duration_minutes ?? 15,
            display_order: displayOrder,
          });
        }
      }

      for (const orig of originalAgenda) {
        if (!nextIds.has(orig.id)) {
          await meetingAgendaService.delete(orig.id);
        }
      }

      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to update meeting",
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const goNext = useCallback(() => {
    if (step === 1) {
      if (!step1Valid) return;
      // UVST: format-level validation (engine) for the Basics step before advancing.
      const ok = vh.validateAll([
        { name: "meeting_title", code: 109, value: form.meeting_title, required: true },
        { name: "location", code: 109, value: form.location },
        { name: "scheduled_date", code: 305, value: form.scheduled_date, required: true },
      ]);
      if (!ok) return;
    }
    if (step === 2 && !step2Valid) return;
    if (step < 3) setStep(((step + 1) as 1 | 2 | 3));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, step1Valid, step2Valid, form.meeting_title, form.location, form.scheduled_date]);

  const goBack = useCallback(() => {
    if (step > 1) setStep(((step - 1) as 1 | 2 | 3));
  }, [step]);

  // Jump straight to a step from the indicator. Backward is always allowed;
  // forward is gated by the same validity rules as Next (basics before
  // participants, participants before agenda).
  const goToStep = useCallback(
    (n: 1 | 2 | 3) => {
      if (n === step) return;
      if (n >= 2 && !step1Valid) return;
      if (n >= 3 && !step2Valid) return;
      setStep(n);
    },
    [step, step1Valid, step2Valid],
  );

  const stepReachable = (n: 1 | 2 | 3) =>
    n === 1 || (n >= 2 && step1Valid && (n < 3 || step2Valid));

  const handleSave = useCallback(() => {
    if (!step3Valid) return;
    // UVST: final format-level validation gate (engine) across the meeting
    // header and every agenda row before submit.
    const ok = vh.validateAll([
      { name: "meeting_title", code: 109, value: form.meeting_title, required: true },
      { name: "location", code: 109, value: form.location },
      { name: "scheduled_date", code: 305, value: form.scheduled_date, required: true },
      { name: "meeting_description", code: 110, value: form.meeting_description },
      ...agenda.flatMap((a, idx) => [
        { name: `agenda_${idx}_name`, code: 109, value: a.agenda_name, required: true },
        { name: `agenda_${idx}_description`, code: 110, value: a.agenda_description || "" },
      ]),
    ]);
    if (!ok) {
      // Surface the block: a row (often a pre-existing one the user didn't
      // touch this session) failed format validation, so the submit stalls
      // silently otherwise. Point them at the offending agenda row by 1-based
      // position so they can open and fix it.
      const badIdx = agenda.findIndex(
        (a) =>
          !a.agenda_name?.trim() ||
          !validateCode(109, a.agenda_name).valid,
      );
      setError(
        badIdx >= 0
          ? `Agenda item ${badIdx + 1} ("${agenda[badIdx].agenda_name}") has an invalid name. Open it and fix the highlighted field before saving.`
          : "Please fix the highlighted fields before saving.",
      );
      return;
    }
    if (mode === "create") return handleCreate();
    return handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, step3Valid, form, participants, agenda, originalAgenda, originalParticipants, meeting]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const StepIndicator = (
    <div className="create-meeting-wizard__step-indicator" role="tablist">
      {([1, 2, 3] as const).map((n) => {
        const labels = ["Basics", "Participants", "Agenda"];
        const isActive = step === n;
        const isComplete = step > n;
        const reachable = stepReachable(n);
        return (
          <button
            key={n}
            type="button"
            className={
              "create-meeting-wizard__step-num" +
              (isActive ? " create-meeting-wizard__step-num--active" : "") +
              (isComplete ? " create-meeting-wizard__step-num--complete" : "")
            }
            role="tab"
            aria-selected={isActive}
            onClick={() => goToStep(n)}
            disabled={!reachable}
            data-testid={`STEP_meeting_wizard_${n}`}
          >
            <span className="create-meeting-wizard__step-num-circle">
              {isComplete ? <Check size={12} /> : n}
            </span>
            <span className="create-meeting-wizard__step-num-label">
              {labels[n - 1]}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const wizardBody = (
        <div className="create-meeting-wizard">
          {/* Step indicator */}
          <div className="create-meeting-wizard__header">
            <div className="create-meeting-wizard__step-label">
              Step {step} of 3 —{" "}
              {step === 1
                ? "Basics"
                : step === 2
                  ? "Participants"
                  : "Agenda"}
            </div>
            {StepIndicator}
          </div>

          {error && (
            <div className="create-meeting-wizard__error" role="alert">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                className="create-meeting-wizard__error-close"
                aria-label="Dismiss"
                data-testid="BTN_meeting_wizard_error_dismiss"
              >
                <XIcon size={14} />
              </button>
            </div>
          )}

          {loadingEdit && (
            <div className="create-meeting-wizard__loading">
              Loading meeting details…
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 1 — BASICS                                              */}
          {/* ============================================================ */}
          {step === 1 && (
            <div className="create-meeting-wizard__step-body">
              <div className="create-meeting-wizard__grid">
                <ValidatedInput
                  vh={vh}
                  name="meeting_title"
                  code={109}
                  required
                  label="Meeting Title"
                  value={form.meeting_title}
                  onChange={(e) => setField("meeting_title", e.target.value)}
                  placeholder="e.g., Q4 Board Meeting"
                  data-testid="INP_meeting_wizard_title"
                />
                <FormControl.SearchSelect
                  label="Meeting Type"
                  required
                  value={form.meeting_type}
                  onChange={(v) =>
                    setField("meeting_type", v == null ? "" : String(v))
                  }
                  options={MEETING_TYPE_OPTIONS}
                  placeholder="Select a meeting type"
                  data-testid="SEL_meeting_wizard_type"
                />
                <ValidatedDatePicker
                  vh={vh}
                  name="scheduled_date"
                  code={305}
                  required
                  label="Scheduled Date"
                  minDate={new Date()}
                  value={form.scheduled_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Switching (back) to today with a start time that has
                    // already passed would leave an unpickable past time
                    // selected — roll the slot forward.
                    setForm((prev) => {
                      const next = { ...prev, scheduled_date: v };
                      if (v === todayIso() && prev.scheduled_time_from < nowHHmm()) {
                        const t = defaultMeetingTimes();
                        next.scheduled_time_from = t.from;
                        next.scheduled_time_to = t.to;
                      }
                      return next;
                    });
                  }}
                  data-testid="INP_meeting_wizard_date"
                />
                {/* Time grouped as From–To pair under a single label so the
                    label "Time To" disappears; Location sits in the next grid
                    cell so the two read together (UAT 2026-05-17). */}
                <FormControl.FormGroup label="Time" required>
                  <div className="create-meeting-wizard__time-pair">
                    <FormControl.TimePicker
                      value={form.scheduled_time_from}
                      minTime={
                        form.scheduled_date === todayIso() ? nowHHmm() : undefined
                      }
                      onChange={(e) => {
                        const from = e.target.value;
                        // Keep the pair ordered: end can never precede start.
                        setForm((prev) => ({
                          ...prev,
                          scheduled_time_from: from,
                          scheduled_time_to:
                            prev.scheduled_time_to && prev.scheduled_time_to < from
                              ? from
                              : prev.scheduled_time_to,
                        }));
                      }}
                      noMargin
                      data-testid="INP_meeting_wizard_time_from"
                    />
                    <span className="create-meeting-wizard__time-sep">
                      to
                    </span>
                    <FormControl.TimePicker
                      value={form.scheduled_time_to}
                      minTime={form.scheduled_time_from || undefined}
                      onChange={(e) =>
                        setField("scheduled_time_to", e.target.value)
                      }
                      noMargin
                      data-testid="INP_meeting_wizard_time_to"
                    />
                  </div>
                </FormControl.FormGroup>
                <ValidatedInput
                  vh={vh}
                  name="location"
                  code={109}
                  label="Location"
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                  placeholder="Conference Room A"
                  data-testid="INP_meeting_wizard_location"
                />
                <FormControl.Input
                  label="Meeting Link"
                  type="url"
                  value={form.meeting_link}
                  onChange={(e) => setField("meeting_link", e.target.value)}
                  placeholder="https://meet.google.com/... or https://teams.microsoft.com/..."
                  data-testid="INP_meeting_wizard_link"
                />
                {/* Voting config folded into Basics (was a separate step).
                    Radios laid out inline to keep it compact. */}
                <FormControl.RadioGroup
                  label="Vote Model"
                  name="vote_model"
                  inline
                  className="create-meeting-wizard__grid-col-2"
                  value={form.vote_model}
                  onChange={(v) =>
                    setField("vote_model", (v as MeetingVoteModel) || "BINARY")
                  }
                  options={VOTE_MODEL_OPTIONS}
                  data-testid="RAD_meeting_wizard_vote_model"
                />
                <FormControl.RadioGroup
                  label="Vote Threshold"
                  name="vote_threshold"
                  inline
                  className="create-meeting-wizard__grid-col-2"
                  value={form.vote_threshold}
                  onChange={(v) =>
                    setField(
                      "vote_threshold",
                      (v as MeetingVoteThreshold) || "SIMPLE_MAJORITY",
                    )
                  }
                  options={VOTE_THRESHOLD_OPTIONS}
                  data-testid="RAD_meeting_wizard_vote_threshold"
                />
                <ValidatedTextarea
                  vh={vh}
                  name="meeting_description"
                  code={110}
                  label="Description"
                  className="create-meeting-wizard__grid-col-2"
                  value={form.meeting_description}
                  onChange={(e) =>
                    setField("meeting_description", e.target.value)
                  }
                  rows={2}
                  placeholder="Brief description or context"
                  data-testid="INP_meeting_wizard_description"
                />
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 2 — PARTICIPANTS                                        */}
          {/* ============================================================ */}
          {step === 2 && (
            <div className="create-meeting-wizard__step-body">
              {/* Add users with one picker; designate the chair with a
                  per-row "Set as chair" button. No separate chairman field. */}
              <div className="create-meeting-wizard__participant-picker">
                <FormControl.MultiSelect
                  value={pendingUserIds}
                  onChange={setPendingUserIds}
                  options={pickableUserOptions}
                  placeholder="Search and pick users"
                  data-testid="SEL_meeting_wizard_participant_pick"
                />
                <Button
                  variant="fill"
                  icon={Plus}
                  onClick={handleAddParticipant}
                  disabled={pendingUserIds.length === 0}
                  data-testid="BTN_meeting_wizard_participant_add"
                >
                  Add
                </Button>
              </div>

              <div className="create-meeting-wizard__participants-header">
                Participants ({participants.length + (form.chairman_id != null ? 1 : 0)})
              </div>
              <div className="create-meeting-wizard__participants-list create-meeting-wizard__participants-list--rows">
                {form.chairman_id != null && (
                  <div className="create-meeting-wizard__participant-row create-meeting-wizard__participant-row--chair">
                    <span className="create-meeting-wizard__participant-name">
                      {userNameById.get(form.chairman_id) ||
                        `User #${form.chairman_id}`}
                    </span>
                    <span className="create-meeting-wizard__participant-role">
                      Chair
                    </span>
                    <button
                      type="button"
                      className="create-meeting-wizard__participant-remove"
                      onClick={() => setField("chairman_id", null)}
                      aria-label="Remove chair"
                      data-testid="BTN_meeting_wizard_chair_remove"
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                )}
                {participants.map((uid) => (
                  <div
                    key={uid}
                    className="create-meeting-wizard__participant-row"
                  >
                    <span className="create-meeting-wizard__participant-name">
                      {userNameById.get(uid) || `User #${uid}`}
                    </span>
                    <button
                      type="button"
                      className="create-meeting-wizard__participant-chair-btn"
                      onClick={() => setAsChair(uid)}
                      data-testid={`BTN_meeting_wizard_set_chair_${uid}`}
                    >
                      Set as chair
                    </button>
                    <button
                      type="button"
                      className="create-meeting-wizard__participant-remove"
                      onClick={() => handleRemoveParticipant(uid)}
                      aria-label="Remove participant"
                      data-testid={`BTN_meeting_wizard_participant_remove_${uid}`}
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                ))}
                {participants.length === 0 && form.chairman_id == null && (
                  <div className="create-meeting-wizard__empty">
                    No participants yet. Search above to add.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* STEP 3 — AGENDA                                              */}
          {/* ============================================================ */}
          {step === 3 && (
            <div className="create-meeting-wizard__step-body">
              <div className="create-meeting-wizard__agenda-header">
                <p className="create-meeting-wizard__hint">
                  Pre-load the agenda for this meeting. You can also add agenda
                  items later from the meeting detail view.
                </p>
                <Button
                  variant="fill"
                  icon={Plus}
                  onClick={openNewAgenda}
                  data-testid="BTN_meeting_wizard_agenda_add"
                >
                  Add agenda item
                </Button>
              </div>

              {agenda.length === 0 ? (
                <div className="create-meeting-wizard__empty create-meeting-wizard__empty--agenda">
                  No agenda items yet. Click "Add agenda item" to add one. You
                  can also add agenda items later from the meeting detail view.
                </div>
              ) : (
                <div className="create-meeting-wizard__agenda-list">
                  {agenda.map((a, idx) => {
                    const linkCount = a.links.length;
                    const presenterName =
                      a.presenter_id != null
                        ? userNameById.get(a.presenter_id) || a.presenter
                        : a.presenter;
                    const meta = [
                      a.agenda_type || "Discussion",
                      `${a.duration_minutes ?? 15} min`,
                      ...(presenterName ? [presenterName] : []),
                      ...(linkCount > 0
                        ? [`${linkCount} link${linkCount > 1 ? "s" : ""}`]
                        : []),
                    ].join(" · ");
                    return (
                      <div
                        key={idx}
                        className="create-meeting-wizard__agenda-row"
                      >
                        <span className="create-meeting-wizard__agenda-num">
                          {idx + 1}
                        </span>
                        <button
                          type="button"
                          className="create-meeting-wizard__agenda-row-main"
                          onClick={() => openEditAgenda(idx)}
                          data-testid={`BTN_meeting_wizard_agenda_row_${idx}_edit`}
                        >
                          <span className="create-meeting-wizard__agenda-row-title">
                            {a.agenda_name.trim() || "Untitled agenda item"}
                          </span>
                          <span className="create-meeting-wizard__agenda-row-meta">
                            {meta}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
  );

  // Agenda item editor popup — add/edit one agenda item without crowding the
  // wizard step. Commits the draft to `agenda` only on Submit/Update; the
  // header X discards. Same fields + link rules as before, just relocated.
  const agendaEditorOpen = agendaEditIdx !== null;
  const isNewAgenda = agendaEditIdx === -1;
  // View-first: existing items are read-only until "Edit" is clicked.
  const agendaFieldsDisabled = !isNewAgenda && !agendaEditing;
  const agendaEditorModal = (
    <Modal isOpen={agendaEditorOpen} onClose={closeAgendaModal} size="md">
      <Modal.Header>
        {isNewAgenda
          ? "Create Agenda Item"
          : agendaEditing
            ? "Edit Agenda Item"
            : "View Agenda Item"}
      </Modal.Header>
      <Modal.Body>
        {agendaDraft && (
          <form
            id="meeting-agenda-item-form"
            onSubmit={(e) => {
              e.preventDefault();
              saveAgendaDraft();
            }}
            className="create-meeting-wizard__agenda-grid"
          >
            <ValidatedInput
              vh={vh}
              name="agenda_modal_name"
              code={109}
              label="Agenda Name"
              required
              value={agendaDraft.agenda_name}
              onChange={(e) => patchDraft({ agenda_name: e.target.value })}
              placeholder="Item name"
              disabled={agendaFieldsDisabled}
              className="create-meeting-wizard__agenda-grid-col-2"
              data-testid="INP_meeting_wizard_agenda_modal_name"
            />
            <FormControl.SearchSelect
              label="Type"
              value={agendaDraft.agenda_type || "Discussion"}
              onChange={(v) =>
                patchDraft({
                  agenda_type: v == null ? "Discussion" : String(v),
                })
              }
              options={AGENDA_TYPE_OPTIONS}
              disabled={agendaFieldsDisabled}
              data-testid="SEL_meeting_wizard_agenda_modal_type"
            />
            <FormControl.SearchSelect
              label="Presenter"
              value={
                agendaDraft.presenter_id == null
                  ? ""
                  : String(agendaDraft.presenter_id)
              }
              onChange={(v) =>
                patchDraft({
                  presenter_id: v == null || v === "" ? null : Number(v),
                })
              }
              options={presenterOptions}
              placeholder={
                presenterOptions.length
                  ? "Select a participant"
                  : "Add participants first"
              }
              disabled={agendaFieldsDisabled}
              data-testid="SEL_meeting_wizard_agenda_modal_presenter"
            />
            <FormControl.Input
              label="Duration (min)"
              type="number"
              value={String(agendaDraft.duration_minutes ?? 15)}
              onChange={(e) =>
                patchDraft({
                  duration_minutes: parseInt(e.target.value, 10) || 15,
                })
              }
              disabled={agendaFieldsDisabled}
              data-testid="INP_meeting_wizard_agenda_modal_duration"
            />
            <ValidatedTextarea
              vh={vh}
              name="agenda_modal_description"
              code={110}
              label="Description"
              value={agendaDraft.agenda_description || ""}
              onChange={(e) =>
                patchDraft({ agenda_description: e.target.value })
              }
              rows={3}
              placeholder="Optional"
              disabled={agendaFieldsDisabled}
              className="create-meeting-wizard__agenda-grid-col-2"
              data-testid="INP_meeting_wizard_agenda_modal_description"
            />

            {/* Existing-agenda links are read-only inside the wizard — link
                mutations on saved rows live in MeetingDetailModal so the
                wizard stays a single-shot create surface. New rows
                (draft.id undefined) accept edits via the picker. */}
            <div className="create-meeting-wizard__agenda-grid-col-2">
              <div className="create-meeting-wizard__agenda-card__links">
                <span className="create-meeting-wizard__participants-header">
                  {agendaDraft.links.length > 0
                    ? `Linked items (${agendaDraft.links.length})`
                    : "Linked items (optional)"}
                </span>
                <EntityLinksMultiField
                  value={agendaDraft.links}
                  onChange={(next) =>
                    agendaDraft.id == null
                      ? patchDraft({ links: next })
                      : undefined
                  }
                  allowedTypes={linkableTypeCodes}
                  disabled={agendaDraft.id != null || agendaFieldsDisabled}
                />
                {agendaDraft.id != null && (
                  <div className="create-meeting-wizard__agenda-card__links-hint">
                    Link changes on existing agenda items are managed from the
                    meeting detail view.
                  </div>
                )}
              </div>
            </div>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {/* Delete lives bottom-left when editing an existing item; the row
            itself is the edit affordance, so no per-row action buttons. */}
        {!isNewAgenda && (
          <Button
            variant="delete-outline"
            icon={Trash2}
            onClick={() => {
              if (agendaEditIdx != null && agendaEditIdx >= 0) {
                handleRemoveAgendaRow(agendaEditIdx);
              }
              closeAgendaModal();
            }}
            style={{ marginRight: "auto" }}
            data-testid="BTN_meeting_wizard_agenda_modal_delete"
          >
            Delete
          </Button>
        )}
        {/* Create → Submit. */}
        {isNewAgenda && (
          <Button
            type="submit"
            form="meeting-agenda-item-form"
            variant="fill"
            icon={Save}
            disabled={!agendaDraft?.agenda_name.trim()}
            data-testid="BTN_meeting_wizard_agenda_modal_save"
          >
            Submit
          </Button>
        )}
        {/* View mode → "Edit" enables the form. */}
        {!isNewAgenda && !agendaEditing && (
          <Button
            variant="fill"
            icon={Edit2}
            onClick={() => setAgendaEditing(true)}
            data-testid="BTN_meeting_wizard_agenda_modal_edit"
          >
            Edit
          </Button>
        )}
        {/* Edit mode → "Update" saves. */}
        {!isNewAgenda && agendaEditing && (
          <Button
            type="submit"
            form="meeting-agenda-item-form"
            variant="update"
            icon={Check}
            disabled={!agendaDraft?.agenda_name.trim()}
            data-testid="BTN_meeting_wizard_agenda_modal_save"
          >
            Update
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );

  const wizardFooter = (
        <div className="create-meeting-wizard__footer">
          {/* Left cluster — Back appears from step 2. No Cancel: the modal
              header X is the dismiss action (modal-design convention). */}
          <div className="create-meeting-wizard__footer-left">
            {step > 1 && (
              <Button
                variant="outline"
                icon={ArrowLeft}
                onClick={goBack}
                disabled={saving}
                data-testid="BTN_meeting_wizard_back"
              >
                Back
              </Button>
            )}
          </div>
          {/* Right cluster — Next on steps 1-2, primary save on step 3. */}
          <div className="create-meeting-wizard__footer-right">
            {step < 3 ? (
              <Button
                variant="fill"
                onClick={goNext}
                disabled={
                  (step === 1 && !step1Valid) ||
                  (step === 2 && !step2Valid)
                }
                data-testid="BTN_meeting_wizard_next"
              >
                <span>Next</span>
                <ArrowRight size={14} />
              </Button>
            ) : (
              <Button
                variant={mode === "edit" ? "update" : "fill"}
                icon={mode === "edit" ? Check : Save}
                onClick={handleSave}
                disabled={saving || !step3Valid}
                data-testid="BTN_meeting_wizard_save"
              >
                {saving
                  ? mode === "edit"
                    ? "Updating..."
                    : "Submitting..."
                  : mode === "edit"
                    ? "Update meeting"
                    : "Save meeting"}
              </Button>
            )}
          </div>
        </div>
  );

  if (inline) {
    return (
      <>
        {wizardBody}
        {/* When the host gives us a footer slot (a ref'd node inside its real
            Modal.Footer), portal the action row there so it lands in the
            modal's fixed footer bar. Otherwise fall back to rendering it at the
            bottom of the body (legacy inline usage). */}
        {footerSlot ? createPortal(wizardFooter, footerSlot) : wizardFooter}
        {agendaEditorModal}
      </>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Header>
          {mode === "edit" && meeting ? (
            <>
              Edit meeting:{" "}
              <span className="item-highlight">{meeting.meeting_title}</span>
            </>
          ) : (
            "Create meeting"
          )}
        </Modal.Header>
        <Modal.Body>{wizardBody}</Modal.Body>
        <Modal.Footer>{wizardFooter}</Modal.Footer>
      </Modal>
      {agendaEditorModal}
    </>
  );
};

export default CreateMeetingWizard;
